import type { MemoryOrigin } from "@/features/material-compliance/api/types"
import type { ApiCitation } from "./api"

export type CreationSource = "extracted" | "manual" | string

export enum QuestionnaireStatus {
  New = "new",
  Extracting = "extracting",
  Extracted = "extracted",
  GeneratingAnswers = "generating_answers",
  EditAnswers = "edit_answers",
  Completed = "completed",
  Failed = "failed",
}

export enum QuestionnaireStatusErrorCode {
  Timeout = "TIMEOUT",
  FileTooLarge = "FILE_TOO_LARGE",
  UnsupportedFormat = "UNSUPPORTED_FORMAT",
  ParseError = "PARSE_ERROR",
  LlmError = "LLM_ERROR",
  AzureDiError = "AZURE_DI_ERROR",
  VlmError = "VLM_ERROR",
  UploadError = "UPLOAD_ERROR",
  ValidationError = "VALIDATION_ERROR",
  InternalError = "INTERNAL_ERROR",
}

export interface QuestionnaireStatusResponse {
  questionnaire_id: string
  status: QuestionnaireStatus
  filename: string
  created_at: string
  updated_at: string
  error_code: QuestionnaireStatusErrorCode | null
  errorMessage: string | null
  isRetryable: boolean | null
}

export interface AnswerTarget {
  addr: string
  label: string | null
  semantics: AnswerTargetSemantic
  allowedValues: string[] | null
  checkedValue?: string
  uncheckedValue?: string
}

export type AnswerTargetSemantic =
  | "CheckboxOption"
  | "Choice"
  | "Date"
  | "Number"
  | "FreeText"
  | "Yes/No"

export type QuestionType =
  | "free-text"
  | "boolean"
  | "multiple-choice"
  | "multiple-select"

/**
 * Turnus Memory (#3): an answer this organisation already stood behind, for a question
 * that means the same thing as this one.
 *
 * ADVISORY, NEVER APPLIED. It sits beside the answer and is never written into it on
 * render or on mount — a person clicks, and the click runs the ordinary answer-saving
 * call. Absent whenever memory has nothing to say, which is the normal case, and absent
 * entirely for an organisation without TURNUS_MEMORY.
 */
/** One document that backed a remembered answer. NOT `Citation` — capture flattens each
 *  citation to its references' location and origin, because the reference text would bloat
 *  every signal and the payload feeds an idempotency digest. */
export interface MemoryAnswerSource {
  /** What the reader sees. The backend renders it: the stored `location` is a blob of s3
   *  uri, chunk id and bounding polygons, and a screen has no business knowing that shape. */
  label: string
  origin?: string | null
}

/** One side of a contradiction memory refuses to settle. Its own `entryId` so a rating
 *  reports against the side it was aimed at; its own `origin` because the reviewer is being
 *  asked which source they believe. */
export interface MemoryAnswerConflictSide {
  entryId: string
  answer: string | null
  because: string | null
  confidence: number | null
  origin: MemoryOrigin | null
  citations?: MemoryAnswerSource[] | null
}

export interface MemoryAnswerSuggestion {
  surface: string
  /** Null when `conflict` is non-empty — see below. */
  entryId: string | null
  answer: string | null
  /** Why this is offered, in words — "you answered this on <file>, 12 Jan 2026". Never
   *  empty on an ordinary suggestion: the backend withholds any that cannot explain where
   *  it came from. Null on a contradiction, which explains itself per side instead. */
  because: string | null
  confidence: number | null
  sourceRef?: string | null
  /** The collapsible trail: who said this, from what, when. */
  origin?: MemoryOrigin | null
  /** Both sides when two remembered answers disagree. Empty otherwise.
   *
   *  READ THIS FIRST. When it is non-empty, `entryId`, `answer` and `because` are ALL null —
   *  there is no single answer and naming one would be the picking the design refuses. */
  conflict?: MemoryAnswerConflictSide[]
  /** What backed the answer when it was given. Shown WITH it — reusing a compliance answer
   *  without its evidence is how an indefensible answer reaches a customer. */
  citations?: MemoryAnswerSource[] | null
}

export interface QuestionnaireResponsePair {
  id: string
  section: string
  questionnaire: string
  question: string
  context?: string | null
  response: string | null
  manuallyEdited: boolean
  lowConfidence: boolean
  citations: Citation[]
  organization: string
  answerTargetsJson?: AnswerTarget[] | null
  sheetName?: string | null
  creationSource?: CreationSource
  type?: QuestionType
  sort_id?: string | null
  responseConfidence?: number | null
  pdfPlacement?: PdfPlacement
  memory?: MemoryAnswerSuggestion | null
}

export interface LayoutRect {
  x: number
  y: number
  width: number
  height: number
}

export interface Placement {
  x: number
  y: number
  w: number
  h: number
  page: number
  fontSize?: number
}

export interface PlacementWithText {
  bbox: Placement
  text: string
}

export interface PdfPlacement {
  extraction?: {
    labelBbox: Placement | null
    answerBboxHint: Placement | null
    questionBbox: Placement | null
    optionsWithBbox: PlacementWithText[] | null
    fontSize: number
    matchInfo: {
      confidence: number
      method: "exact"
    }
  }
  userPlacement?: UserPlacement | null
}

export interface UserPlacement {
  page: number
  x: number
  y: number
  width: number
  height: number
  fontSize?: number
  text: null | string
  isUserPositioned: boolean
  updatedAt: string
  optionsPlacements: Array<{
    text: string
    x: number
    y: number
    width: number
    height: number
    page: number
  }>
}

export interface QuestionnaireSection {
  id: string
  name: string
  organization: string
  questionnaire: string
  responsePairs: QuestionnaireResponsePair[]
}

export interface SignaturePlacement {
  id: string
  signatureId: string
  page: number
  x: number
  y: number
  width: number
  height: number
}

export interface Questionnaire {
  id: string
  userId: string
  userName: string
  fileName: string
  errorCode: QuestionnaireStatusErrorCode | null
  errorMessage: string
  isRetryable: boolean
  createdAt: Date
  updatedAt: Date
  organization: string
  status: QuestionnaireStatus
  sections: QuestionnaireSection[]
  hasPositionData?: boolean
  // True when this questionnaire was extracted by the Extend AI path (stamped at
  // extraction, serialized by the backend). The preview reads this — NOT the viewer's
  // USE_EXTEND flag — so the interactive overlay always matches the downloaded PDF,
  // regardless of who is viewing.
  usesExtend?: boolean
  // Recall ingestion status, null/undefined for legacy questionnaires that answer
  // through the v1 pipeline. Answering context only applies to ingested (v2)
  // questionnaires.
  ingestionStatus?: string | null
  // Placed org signatures on the PDF answer page. Null/undefined for every
  // questionnaire that has no signature.
  signaturePlacements?: SignaturePlacement[] | null
}

export interface BoundingRegion {
  pageNumber: number
  polygon: number[]
}

export interface Citation {
  sourceFileName: string
  sourceFileId: string
  sourceFilePage: number
  sourceText?: string
  sourceType?:
    | "rag"
    | "web"
    | "mc-data"
    | "company_profile"
    | "answering_context"
    | "help"
  url?: string
  boundingRegions?: BoundingRegion[]
  /** MC chat tool id (e.g. "get_article_details") for mc-data citations. */
  toolName?: string
}

export type QuestionnaireUploadSuccessHandler = (data: Questionnaire) => void
export type QuestionnaireUploadErrorHandler = (error: Error) => void

export interface UpdateQuestionnaireStatusArgs {
  questionnaireId?: string
  status: QuestionnaireStatus
}

export interface EditResponsePairArgs {
  questionnaireId: string
  responsePairId: string
  question?: string
  response?: string
  /** Replaces existing citations on this answer when provided. Omit to leave untouched. */
  citations?: ApiCitation[]
}

export interface EditResponsePairRequestBody {
  question?: string
  response?: string
  citations?: ApiCitation[]
}

export interface UpdateAnswerPlacementArgs {
  questionnaireId: string
  responsePairId: string
  placement: {
    page: number
    x: number
    y: number
    width: number
    height: number
    font_size: number
    text?: string
    options_placements?: Array<{
      text: string
      x: number
      y: number
      width: number
      height: number
      page: number
    }> | null
  }
}
